import root from "window-or-global";

import {
    InContextTranslationPseudoLocale,
    type LocaleString,
} from "@atg-shared/market-config";
import * as Storage from "@atg-shared/storage";
import system from "@atg-shared/system";

import {getLangCode} from "./getLangCode";
import {getLocaleFromUrl} from "./getLocaleFromUrl";

const {defaultLocale} = root.marketConfig ?? {};

export const SELECTED_LANGUAGE_KEY = "SELECTED_LANGUAGE";

function isValidLocale(locale: string): locale is LocaleString {
    return (
        locale.length > 0 &&
        root.marketConfig.availableLocales.includes(locale as LocaleString)
    );
}

/**
 * Returns the current language and locale, based on the following priority:
 * 1. The locale in the URL (only for web)
 * 2. The locale in storage (if previously selected by the user)
 * 3. The default locale for the market
 *
 * If the current locale is the pseudo locale for in-context translation, and `returnTargetLanguageForInContextTranslation` is true (default),
 * the target language for in-context translation will be returned instead.
 */
export function getLanguage(returnTargetLanguageForInContextTranslation = true) {
    const localeFromUrl = system.isWeb ? getLocaleFromUrl() : undefined;
    const localeFromStorage = Storage.getItem(SELECTED_LANGUAGE_KEY);

    // get the preferred locale from URL (only web) or Storage
    const preferredLocale = [localeFromUrl, localeFromStorage].find(
        (l) => l && isValidLocale(l),
    ) as LocaleString | undefined;
    // use preferred locale or fallback to default
    const locale = preferredLocale ?? defaultLocale;

    if (system.isWeb && locale !== localeFromStorage) {
        Storage.setItem(SELECTED_LANGUAGE_KEY, locale);
    }

    if (
        locale === InContextTranslationPseudoLocale &&
        returnTargetLanguageForInContextTranslation
    ) {
        // if the current locale is the pseudo locale for in-context translation, try to get the target language for in-context translation from the Crowdin JIPT object or fallback to the default locale for the market
        const selectedInContextTargetLocale =
            root.marketConfig.availableLocales.find((l) => {
                if ("jipt" in root && root.jipt?.target_language) {
                    return l.startsWith(root.jipt.target_language);
                }
                return false;
            }) ?? root.marketConfig.defaultLocale;

        return {
            locale: selectedInContextTargetLocale,
            lang: getLangCode(selectedInContextTargetLocale),
        };
    }

    return {
        locale,
        lang: getLangCode(locale),
    };
}

/**
 * Saves the locale in storage before the app initialises.
 * Used when the locale is injected via an external mechanism (e.g. a webview query param)
 * rather than being resolved from the URL path or a user preference already in storage.
 */
export function saveToStorage(locale: LocaleString) {
    Storage.setItem(SELECTED_LANGUAGE_KEY, locale);
}
